import * as XLSX from "xlsx";
import { FIELDS, emptyChannel } from "./fields";
import {
  formatContact,
  formatMoney,
  formatReach,
  joinList,
  parseContact,
  parseMoney,
  parseReach,
  splitList,
} from "./parse";
import { formatDateTime } from "./time";

/* ---------- One cell, as text ---------- */

// How a channel's field reads in a sheet cell, and in search
export function cellText(row, field) {
  switch (field.type) {
    case "list":
      return joinList(row[field.key]);
    case "money":
      return formatMoney(row.cpm, row.currency);
    case "reach":
      return formatReach(row.reach);
    case "contact":
      return formatContact(row.contactName, row.contactEmail);
    case "date":
      return row.updatedAt
        ? `${formatDateTime(row.updatedAt)}${row.updatedBy ? ` (${row.updatedBy})` : ""}`
        : "";
    default:
      return row[field.key] || "";
  }
}

/* ---------- Import ---------- */

const normalize = (h) => String(h || "").toLowerCase().replace(/\s+/g, " ").trim();

// The field a heading belongs to: the one with the longest matching prefix,
// so "Channel owner" goes to platform rather than to channel name.
function fieldForHeader(header) {
  const h = normalize(header);
  let best = null;
  let bestLen = 0;
  FIELDS.forEach((f) => {
    f.match.forEach((m) => {
      if (h.startsWith(m) && m.length > bestLen) {
        best = f;
        bestLen = m.length;
      }
    });
  });
  return best;
}

function channelFromCells(cells, columns) {
  const channel = emptyChannel();
  columns.forEach(({ index, field }) => {
    const raw = cells[index];
    switch (field.type) {
      case "list":
        channel[field.key] = splitList(raw);
        break;
      case "money": {
        const { amount, currency } = parseMoney(raw);
        channel.cpm = amount;
        channel.currency = currency;
        break;
      }
      case "reach":
        channel.reach = parseReach(raw);
        break;
      case "contact": {
        const { name, email } = parseContact(raw);
        channel.contactName = name;
        channel.contactEmail = email;
        break;
      }
      default:
        channel[field.key] = String(raw ?? "").trim();
    }
  });
  return channel;
}

// Reads a .csv / .xlsx / .xls file laid out like the inventory sheet.
// Returns the channels plus what was understood, for the import preview.
export async function readInventoryFile(file) {
  const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });

  // The heading row is the first of the top ten that names at least two
  // known columns; anything above it (titles, notes) is ignored.
  let headerIndex = -1;
  let columns = [];
  for (let r = 0; r < Math.min(grid.length, 10); r += 1) {
    const used = new Set();
    const found = [];
    grid[r].forEach((header, index) => {
      const field = fieldForHeader(header);
      if (field && !used.has(field.key)) {
        used.add(field.key);
        found.push({ index, field, header: String(header).trim() });
      }
    });
    if (found.length >= 2) {
      headerIndex = r;
      columns = found;
      break;
    }
  }

  if (headerIndex === -1 || !columns.some((c) => c.field.key === "channelName")) {
    throw new Error(
      "Couldn't find the column headings. The file needs at least a \"Channel name\" column."
    );
  }

  const mapped = new Set(columns.map((c) => c.index));
  const ignored = grid[headerIndex]
    .map((h, i) => ({ h: String(h).trim(), i }))
    .filter(({ h, i }) => h && !mapped.has(i))
    .map(({ h }) => h);

  const channels = [];
  let skipped = 0;
  grid.slice(headerIndex + 1).forEach((cells) => {
    if (!cells.some((c) => String(c).trim())) return; // blank line
    const channel = channelFromCells(cells, columns);
    if (!channel.channelName) {
      skipped += 1;
      return;
    }
    channels.push(channel);
  });

  return { channels, columns, ignored, skipped };
}

/* ---------- Export ---------- */

function workbookFor(rows) {
  const aoa = [
    FIELDS.map((f) => f.sheetHeader),
    ...rows.map((row) => FIELDS.map((f) => cellText(row, f))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = FIELDS.map((f) => ({
    wch: Math.min(
      40,
      Math.max(f.sheetHeader.length, ...rows.map((r) => cellText(r, f).length)) + 2
    ),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Inventory");
  return wb;
}

// bookType: "xlsx" or "csv"
export function exportInventory(rows, bookType, name = "admongrel-inventory") {
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbookFor(rows), `${name}-${stamp}.${bookType}`, { bookType });
}

export function downloadTemplate() {
  XLSX.writeFile(workbookFor([]), "admongrel-inventory-template.xlsx", { bookType: "xlsx" });
}
